/**
 * Google Analytics 4 for the public marketing site.
 *
 * WHY NOT THE SNIPPET IN index.html: the copy-paste gtag block Google hands you
 * puts a googletagmanager.com <script> in the pre-JS window, so every visitor's
 * IP reaches Google before the cookie banner has even painted. That is the exact
 * pattern index.html already refuses for Google Fonts (LG München, 2022), and
 * CookieBanner.tsx's contract — "the pre-JS window contains nothing that needs
 * consent: no third-party scripts, no cookies" — depends on it staying true.
 * So gtag.js is injected lazily, and only once a visitor has affirmatively
 * accepted analytics cookies.
 *
 * SCOPE: marketing routes only. This hook is called from MarketingLayout, never
 * from the dashboard, because authenticated paths carry client identifiers
 * (/clients/:id, /dashboard/...) and a firm's client list is not Google's
 * business. GA4 exists here to measure the traffic → lead → deal funnel that
 * Waypoint's own funnel-attribution feature reports on, and that funnel starts
 * on the public site.
 *
 * LATE CONSENT: a visitor who accepts after landing still gets measured from
 * that point forward — we listen for the same 'cookie-consent:changed' event
 * lib/attribution.ts uses, rather than reading consent once at mount.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { hasAnalyticsConsent } from '../components/layout/CookieBanner';

/** Mirrors CHANGED_EVENT in CookieBanner.tsx. */
const CONSENT_CHANGED_EVENT = 'cookie-consent:changed';

/** Measurement ID. Overridable per environment so a staging deploy can point at
 *  its own property (or at nothing) instead of polluting production numbers. */
export const GA4_MEASUREMENT_ID: string =
  (import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined) ?? 'G-9T5P74Z2EH';

type GtagArgs = [string, ...unknown[]];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagArgs) => void;
  }
}

let scriptInjected = false;

/** True once gtag.js has been asked for. Exported for tests. */
export function ga4Loaded(): boolean {
  return scriptInjected;
}

/**
 * Inject gtag.js and configure the property. Idempotent, and a no-op without
 * consent or a measurement ID.
 */
function loadGa4(): void {
  if (scriptInjected) return;
  if (!GA4_MEASUREMENT_ID) return;
  if (!hasAnalyticsConsent()) return;
  if (typeof document === 'undefined') return;

  scriptInjected = true;

  window.dataLayer = window.dataLayer || [];
  // Must stay a function declaration pushing `arguments`: gtag relies on the
  // arguments object identity, so a (...args) => dataLayer.push(args) arrow
  // silently records the wrong shape.
  window.gtag = function gtag() {
    window.dataLayer!.push(arguments);
  } as (...args: GtagArgs) => void;

  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    // We send page_view ourselves on each route change; the automatic one fires
    // before React Router has resolved the path and double-counts the landing.
    send_page_view: false,
    anonymize_ip: true,
    // This is a B2B funnel measurement, not an ad audience. Turning both off
    // keeps GA4 out of Google's advertising identity graph, which is what lets
    // the privacy policy describe this as analytics rather than ad tracking.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_MEASUREMENT_ID)}`;
  document.head.appendChild(s);
}

/** Report one SPA route change. No-op until gtag is loaded. */
function sendPageView(path: string): void {
  if (!scriptInjected || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: document.title,
  });
}

/**
 * Load GA4 (post-consent) and report marketing page views.
 *
 * Call from MarketingLayout only — see the SCOPE note above.
 */
export function useGa4(): void {
  const { pathname, search } = useLocation();
  // Router gives us a fresh pathname before consent may exist; remember the last
  // one so a late "accept" can report the page the visitor is actually on rather
  // than silently starting from their second page.
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const path = pathname + search;
    lastPath.current = path;

    loadGa4();
    sendPageView(path);
  }, [pathname, search]);

  useEffect(() => {
    function onConsentChanged() {
      // Re-read consent rather than trusting the event detail: hasAnalyticsConsent()
      // also honours a GPC opt-out that the stored choice knows nothing about.
      if (!hasAnalyticsConsent()) return;
      loadGa4();
      if (lastPath.current) sendPageView(lastPath.current);
    }

    try {
      window.addEventListener(CONSENT_CHANGED_EVENT, onConsentChanged);
      return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onConsentChanged);
    } catch {
      /* no addEventListener (SSR/test harness) — upgrade simply never fires */
      return undefined;
    }
  }, []);
}
